// Pure helpers for the operator-only AI fallback diagnostics view. The backend
// returns an allowlisted, PII-free entry shape; these helpers only ever read
// the allowlisted fields, never fabricate a reason, and classify transport
// errors so the UI can tell "backend not published" (404) from "access denied"
// (403) and from an expired session (401, already handled by the API client).

export const DIAG_UNKNOWN = "unknown";

// The only fields the UI is allowed to surface (matches the backend allowlist).
export const DIAG_ALLOWLIST = [
  "id", "room_id", "message_id", "group_key", "role", "action", "trigger",
  "direct", "language_source", "language_reason", "data_state",
  "source_game_id", "created_at",
  "language_stage", "language_elapsed_ms", "language_timeout_ms",
  "language_error_category", "language_http_status",
];

// A missing/empty value is rendered as "unknown" — never invented.
export function diagValue(v) {
  if (v === undefined || v === null) return DIAG_UNKNOWN;
  if (typeof v === "string" && v.trim() === "") return DIAG_UNKNOWN;
  return v;
}

// Millisecond durations: a real finite number (INCLUDING 0) is preserved;
// null / absent / non-numeric → "unknown".
export function diagMs(v) {
  if (typeof v === "number" && Number.isFinite(v)) return `${v} ms`;
  return DIAG_UNKNOWN;
}

// Grouping semantics:
//   - group_key field absent (legacy rows)   → "unknown"
//   - explicit null                          → standalone / non-group
//   - any other value                        → the group id
export function groupInfo(entry) {
  const e = entry || {};
  if (!Object.prototype.hasOwnProperty.call(e, "group_key")) return { kind: "unknown", label: DIAG_UNKNOWN };
  if (e.group_key === null) return { kind: "standalone", label: "non-group" };
  if (e.group_key === undefined || e.group_key === "") return { kind: "unknown", label: DIAG_UNKNOWN };
  return { kind: "group", label: String(e.group_key) };
}

// Builds the query object for TurfTalk.adminAiDiagnostics. Empty filters are
// omitted; `before` (opaque cursor) is passed through unchanged — the API
// client's qs() applies proper URL encoding.
export function buildDiagnosticsQuery({ roomId, gameId, limit = 50, before } = {}) {
  const q = {};
  const rid = (roomId || "").trim(); if (rid) q.room_id = rid;
  const gid = (gameId || "").trim(); if (gid) q.game_id = gid;
  q.limit = limit;
  if (before) q.before = before;
  return q;
}

export function classifyDiagnosticsError(err) {
  if (err && err.name === "AuthExpiredError") return { kind: "auth", message: "Session expired — please sign in again." };
  const status = err && err.status;
  if (status === 403) return { kind: "forbidden", message: "Access denied — your account does not have operator permission for diagnostics." };
  if (status === 404) return { kind: "unpublished", message: "Diagnostics API not found — the backend endpoint is not published yet." };
  return { kind: "error", message: (err && err.message) || "Failed to load diagnostics." };
}

export function formatDiagTime(iso) {
  if (!iso) return DIAG_UNKNOWN;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}
